import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Input, Label, FormGroup, Button } from 'reactstrap';
import ConfirmCloseModal from '../Common/ConfirmCloseModal';
import ErrorAlert from '../Common/ErrorAlert';
import Select from "react-select";
import { useDispatch, useSelector } from 'react-redux';
import {
    getLegacyPatentes,
    getLegacyPersonas,
    getLegacyTiposTrabajoOt
} from '../../slices/thunks';
import useUpdateRecepcionVehiculo from '../Hooks/useUpdateRecepcionVehiculo';
import useRecepcionVehiculoPorId from '../Hooks/useRecepcionVehiculoPorId';
import useReceptionEditForm from '../Hooks/useReceptionEditForm';
import ContactSection from './sections/ContactSection';
import InventorySection from './sections/InventorySection';
import DamageSection from './sections/DamageSection';
import SignatureSection from './sections/SignatureSection';
import FractionSlider from '../Common/FractionSlider';
import ImageUploader from '../Common/ImageUploader';

const ModalModificarRecepcionVehiculo = ({ isOpen, toggle, onSave, selectedRecepcion }) => {
    const tenantId = useSelector((state) => state.Context.tenantId);
    const empresa = useSelector((state) => state.Context.empresa);
    const departamento = useSelector((state) => state.Context.departamento);
    const userInfo = useSelector((state) => state.UserInfo.userInfo);

    const patentes = useSelector((state) => state.LegacyPatentes.legacyPatentes);
    const tiposTrabajoOt = useSelector((state) => state.LegacyTiposTrabajoOt.legacyTiposTrabajoOt);
    const dispatch = useDispatch();

    const { recepcion, loading: loadingRecepcion, error: errorRecepcion } = useRecepcionVehiculoPorId(selectedRecepcion);
    const { updateRecepcionVehiculo, loading: updating } = useUpdateRecepcionVehiculo();

    const {
        formData,
        errors,
        handleChange,
        handleInventoryChange,
        handleDamagesChange,
        handleFuelChange,
        handleSignatureChange,
        handleImagesChange,
        validate,
        isDirty,
    } = useReceptionEditForm(recepcion);

    const [apiError, setApiError] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [tipoTrabajoOt, setTipoTrabajoOt] = useState({});

    useEffect(() => {
        if (isOpen) {
            dispatch(getLegacyPatentes({ tenantId, empresa }));
            dispatch(getLegacyPersonas({ tenantId, empresa }));
            dispatch(getLegacyTiposTrabajoOt({ tenantId, empresa }));
            setApiError('');
        }
    }, [isOpen, tenantId, empresa, dispatch]);

    useEffect(() => {
        if (recepcion && recepcion.tipo_trabajo_ot) {
            const found = Array.isArray(tiposTrabajoOt)
                ? tiposTrabajoOt.find(m => m.CODREG === recepcion.tipo_trabajo_ot)
                : null;
            if (found) {
                setTipoTrabajoOt({ value: found.CODREG, label: found.DESCRI });
            }
        }
    }, [recepcion, tiposTrabajoOt]);

    const handleSelectTiposTrabajoOt = (opcion) => {
        setTipoTrabajoOt(opcion);
        handleChange({ target: { name: 'tipo_trabajo_ot', value: opcion?.value || '' } });
    };

    function parseTiposTrabajoOt(tipo) {
        return { value: tipo?.CODREG, label: tipo?.DESCRI };
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');

        if (!validate()) {
            return;
        }

        const result = await updateRecepcionVehiculo(
            selectedRecepcion,
            formData,
            userInfo?.name
        );

        if (result.success) {
            onSave();
            toggle();
        } else {
            setApiError(result.error || 'Error al actualizar la recepción');
        }
    };

    const handleCancel = () => {
        if (isDirty) {
            setShowConfirm(true);
            return;
        }
        toggle();
    };

    const handleConfirmClose = () => {
        setShowConfirm(false);
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
            <ModalHeader toggle={toggle}>
                <i className="ri-car-line me-2"></i>
                Modificar Recepción de Vehículo
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {loadingRecepcion ? (
                        <div className="text-center py-4">
                            <i className="ri-loader-4-line ri-spin-line me-2"></i>
                            Cargando datos de la recepción...
                        </div>
                    ) : errorRecepcion ? (
                        <ErrorAlert error={errorRecepcion} />
                    ) : (
                        <>
                            {apiError && (
                                <ErrorAlert error={apiError} onDismiss={() => setApiError('')} />
                            )}

                            <ContactSection
                                formData={formData}
                                onChange={handleChange}
                                errors={errors}
                                showPatente={true}
                            />

                            <div className="d-flex align-items-center my-2">
                                <div className="flex-grow-1 border-top"></div>
                                <span className="mx-3 text-muted fw-bold">Vehículo</span>
                                <div className="flex-grow-1 border-top"></div>
                            </div>

                            <div className="row align-items-end">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="odometro">Odómetro</Label>
                                        <Input
                                            type="number"
                                            id="odometro"
                                            name="odometro"
                                            value={formData.odometro || ''}
                                            onChange={handleChange}
                                            placeholder="Kilometraje"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="emisora_radio">Emisora radio</Label>
                                        <Input
                                            type="text"
                                            id="emisora_radio"
                                            name="emisora_radio"
                                            value={formData.emisora_radio || ''}
                                            onChange={handleChange}
                                            placeholder="Ej: 88.1 FM"
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row align-items-end">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="tipo_trabajo">Tipo Trabajo</Label>
                                        <Select
                                            id="tipo_trabajo"
                                            placeholder="Seleccione..."
                                            value={tipoTrabajoOt}
                                            onChange={handleSelectTiposTrabajoOt}
                                            options={Array.isArray(tiposTrabajoOt) && tiposTrabajoOt.length > 0 ? tiposTrabajoOt.map(parseTiposTrabajoOt) : []}
                                            onFocus={(e) => { e.target.readOnly = true; }}
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="nivel_combustible">Combustible</Label>
                                        <FractionSlider
                                            value={formData.nivel_estanque}
                                            onChange={handleFuelChange}
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col">
                                    <FormGroup>
                                        <Label for="solicitud_cliente">Solicitud cliente</Label>
                                        <Input
                                            type="textarea"
                                            id="solicitud_cliente"
                                            name="solicitud_cliente"
                                            value={formData.solicitud_cliente || ''}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            <InventorySection
                                inventory={formData.inventario}
                                sinInventario={formData.sin_inventario}
                                onInventoryChange={handleInventoryChange}
                                onSinInventarioChange={(e) => {
                                    handleChange({ target: { name: 'sin_inventario', value: e.target.checked } });
                                }}
                            />

                            <DamageSection
                                danos={formData.danos}
                                sinDanos={formData.sin_danos}
                                sucio={formData.sucio}
                                onDamagesChange={handleDamagesChange}
                                onSinDanosChange={(e) => {
                                    handleChange({ target: { name: 'sin_danos', value: e.target.checked } });
                                }}
                                onSucioChange={(e) => {
                                    handleChange({ target: { name: 'sucio', value: e.target.checked } });
                                }}
                            />

                            <div className="d-flex align-items-center my-2">
                                <div className="flex-grow-1 border-top"></div>
                                <span className="mx-3 text-muted fw-bold">Imágenes</span>
                                <div className="flex-grow-1 border-top"></div>
                            </div>
                            <ImageUploader
                                onImagesChange={handleImagesChange}
                                maxImages={10}
                                existingImages={formData.imagenes}
                            />

                            <SignatureSection
                                firmaCliente={formData.firma_cliente}
                                firmaRecepcionista={formData.firma_recepcionista}
                                onSignatureChange={handleSignatureChange}
                            />

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="nombre_recepcionista">Recepcionista</Label>
                                        <Input
                                            type="text"
                                            id="nombre_recepcionista"
                                            name="nombre_recepcionista"
                                            value={formData.nombre_recepcionista || ''}
                                            onChange={handleChange}
                                            placeholder="Ej: Miguel Tapia"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="quien_retira">Quién retira</Label>
                                        <Input
                                            type="text"
                                            id="quien_retira"
                                            name="quien_retira"
                                            value={formData.quien_retira || ''}
                                            onChange={handleChange}
                                            placeholder="Nombre de la persona"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-light" onClick={handleCancel}>
                        Cancelar
                    </button>
                    <Button type="submit" color="success" disabled={updating || loadingRecepcion || errorRecepcion}>
                        {updating ? (
                            <>
                                <i className="ri-loader-4-line ri-spin-line me-1"></i>
                                Actualizando...
                            </>
                        ) : (
                            <>
                                <i className="ri-save-line me-1"></i>
                                Actualizar
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Form>
            <ConfirmCloseModal
                isOpen={showConfirm}
                toggle={() => setShowConfirm(false)}
                onConfirm={handleConfirmClose}
            />
        </Modal>
    );
};

export default ModalModificarRecepcionVehiculo;
